import React, { useRef, useState, useContext, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ToastContext from '../contexts/ToastContext';
import ProfileStats from './ProfileStats';

export default function Profile() {
  const { currentUser, updateProfile, updateEmail, updatePassword, logout } = useAuth();
  const emailRef = useRef();
  const fileInputRef = useRef();
  const [displayName, setDisplayName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [country, setCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [photoPreview, setPhotoPreview] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const toast = useContext(ToastContext);

  useEffect(() => {
    if (currentUser) {
      setDisplayName(currentUser.displayName || '');
      setFirstName(currentUser.firstName || '');
      setLastName(currentUser.lastName || '');
      setDateOfBirth(currentUser.dateOfBirth ? new Date(currentUser.dateOfBirth).toISOString().split('T')[0] : '');
      setGender(currentUser.gender || '');
      setCountry(currentUser.country || '');
      setPhone(currentUser.phone || '');
      setPhotoURL(currentUser.photoURL || '');
      setPhotoPreview(currentUser.photoURL || '');
    }
  }, [currentUser]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        if (toast && toast.error) toast.error('Veuillez sélectionner une image');
        return;
      }

      // Vérifier la taille (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        if (toast && toast.error) toast.error('L\'image ne doit pas dépasser 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoURL(reader.result);
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  async function handleSaveAll(e) {
    e.preventDefault();
    setMessage('');
    const ops = [];
    const newEmail = emailRef.current.value;
    const newPassword = password;
    const newConfirm = confirmPassword;

    try {
      // Profile update
      if (displayName !== (currentUser.displayName || '') || photoURL !== (currentUser.photoURL || '') || firstName !== (currentUser.firstName || '') || lastName !== (currentUser.lastName || '') || dateOfBirth !== (currentUser.dateOfBirth ? new Date(currentUser.dateOfBirth).toISOString().split('T')[0] : '') || gender !== (currentUser.gender || '') || country !== (currentUser.country || '') || phone !== (currentUser.phone || '')) {
        ops.push(updateProfile({ displayName, photoURL, firstName, lastName, dateOfBirth, gender, country, phone }));
      }

      // Email update
      if (newEmail && newEmail !== (currentUser.email || '')) {
        ops.push(updateEmail(newEmail));
      }

      // Password update (only if provided) — require confirmation
      if (newPassword) {
        if (!newConfirm) {
          throw new Error('Veuillez confirmer le nouveau mot de passe');
        }
        if (newPassword !== newConfirm) {
          throw new Error('Les mots de passe ne correspondent pas');
        }
        ops.push(updatePassword(newPassword));
      }

      if (ops.length === 0) {
        setMessage('Aucune modification détectée');
        return;
      }

      await Promise.all(ops);
      // clear password fields after successful save
      setPassword('');
      setConfirmPassword('');
      setMessage('Modifications enregistrées');
      if (toast && toast.success) toast.success('Modifications enregistrées');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erreur lors de la sauvegarde';
      setMessage(msg);
      if (toast && toast.error) toast.error(msg);
    }
  }

  if (!currentUser) return <div>Connectez-vous pour voir votre profil.</div>;

  return (
    <div className="container">
      <ProfileStats userId={currentUser.uid} />
      
      <div className="card" style={{maxWidth:920,margin:'1rem auto',display:'grid',gridTemplateColumns:'220px 1fr',gap:'1.25rem',alignItems:'start'}}>
        <div className="col" style={{alignItems:'center'}}>
          <div className="avatar" style={{width:120,height:120,borderRadius:16,overflow:'hidden'}}>
            {photoPreview ? <img src={photoPreview} alt="Avatar" /> : <div style={{width:'100%',height:'100%',background:'linear-gradient(135deg,var(--accent),var(--accent-2)'}} />}
          </div>
          <div style={{marginTop:12,fontWeight:700}}>{currentUser.displayName || '—'}</div>
          <div className="muted" style={{fontSize:'0.9rem'}}>{currentUser.email}</div>
        </div>

        <div>
          <h2>Mon profil</h2>
          {message && (
            <div className="card headerless" role="status" style={{background:'rgba(16,185,129,0.04)',borderLeft:'4px solid var(--success)',marginBottom:12}}>
              <div style={{color:'var(--success)'}}>{message}</div>
            </div>
          )}

          <form onSubmit={handleSaveAll} className="card headerless">
            <h3>Modifier profil</h3>
            <div className="field">
              <label className="muted">Prénom</label>
              <input className="input" value={firstName} onChange={e => setFirstName(e.target.value)} />
            </div>
            <div className="field">
              <label className="muted">Nom</label>
              <input className="input" value={lastName} onChange={e => setLastName(e.target.value)} />
            </div>
            <div className="field">
              <label className="muted">Nom d'affichage</label>
              <input className="input" value={displayName} onChange={e => setDisplayName(e.target.value)} />
            </div>
            <div className="field">
              <label className="muted">Date de naissance</label>
              <input className="input" type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} min="1960-01-01" max="2016-12-31" />
            </div>
            <div className="field">
              <label className="muted">Genre</label>
              <select className="input" value={gender} onChange={e => setGender(e.target.value)}>
                <option value="">Sélectionnez</option>
                <option value="Homme">Homme</option>
                <option value="Femme">Femme</option>
                <option value="Autre">Autre</option>
                <option value="Préfère ne pas répondre">Préfère ne pas répondre</option>
              </select>
            </div>
            <div className="field">
              <label className="muted">Pays</label>
              <input className="input" value={country} onChange={e => setCountry(e.target.value)} />
            </div>
            <div className="field">
              <label className="muted">Téléphone</label>
              <input className="input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} />
            </div>
            <div className="field">
              <label className="muted">Photo de profil</label>
              <input 
                ref={fileInputRef}
                className="input" 
                type="file" 
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ padding: '0.5rem' }}
              />
              <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                Formats acceptés: JPG, PNG, GIF, WebP. Taille max: 5MB
              </div>
            </div>

            <h3 style={{marginTop:12}}>Modifier email</h3>
            <div className="field">
              <label className="muted">Email</label>
              <input className="input" defaultValue={currentUser.email} ref={emailRef} />
            </div>

            <h3 style={{marginTop:12}}>Changer mot de passe</h3>
            <div className="field">
              <label className="muted">Nouveau mot de passe</label>
              <input className="input" type="password" value={password} onChange={e=>setPassword(e.target.value)} />
            </div>
            <div className="field">
              <label className="muted">Confirmer le mot de passe</label>
              <input className="input" type="password" value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} />
              <div style={{marginTop:8,fontSize:'0.9rem'}}>
                {confirmPassword.length > 0 && (
                  password === confirmPassword ? <span style={{color:'var(--success)'}}>Les mots de passe correspondent</span> : <span style={{color:'var(--danger)'}}>Les mots de passe ne correspondent pas</span>
                )}
              </div>
            </div>

            <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}>
              <button className="btn btn-primary" type="submit">Confirmer les modifications</button>
            </div>
          </form>

          <div style={{marginTop:12,display:'flex',justifyContent:'flex-end'}}>
            <button className="btn btn-ghost" onClick={logout}>Se déconnecter</button>
          </div>
        </div>
      </div>
    </div>
  );
}
